import { LINK_VERSION_OPTIONS, LINK_VERSION_QUESTION } from '@/data/linkVersions'
import { hasProductLine } from '@/data/productLines'
import { componentsFor } from '@/data/traverseComponents'
import { effectiveServerMode, useInfrastructureWizard, type AddOnKey } from '@/stores/infrastructureWizard'
import type { ImageComponent, NetworkChoice } from '@/types/infrastructure'
import { asksAnalyticsAddOns } from '@/utils/infraValidation'
import page from '@/pages/NewDeploymentPage.module.css'
import styles from './Infrastructure.module.css'
import { GoldenImageSelect } from './GoldenImageSelect'
import { IisAddOnCard } from './IisAddOnCard'
import { NetworkSelect } from './NetworkSelect'
import { OptionCard } from './OptionCard'

interface AddOnCardProps {
  addOnKey: AddOnKey
  /** The question, and the name used in labels. */
  question: string
  name: string
  /** Which image list this add-on uses. Today every component shares the main list. */
  component: ImageComponent
  /** The network already chosen for the main server, offered as the starting point when it exists here. */
  suggestedNetwork: NetworkChoice | null
}

function AddOnCard({ addOnKey, question, name, component, suggestedNetwork }: AddOnCardProps) {
  const product = useInfrastructureWizard((s) => s.product)
  const environment = useInfrastructureWizard((s) => s.environment)
  const region = useInfrastructureWizard((s) => s.region)
  const resourceGroup = useInfrastructureWizard((s) => s.resourceGroup)
  const addOn = useInfrastructureWizard((s) => s[addOnKey])
  const setEnabled = useInfrastructureWizard((s) => s.setAddOnEnabled)
  const patch = useInfrastructureWizard((s) => s.patchAddOn)
  if (!product) return null

  return (
    <section className={styles.section} aria-label={name}>
      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend className={styles.legend}>{question}</legend>
        <div className={page.radioGroup}>
          <label className={page.radioOption}>
            <input type="radio" name={`addon-${addOnKey}`} checked={addOn.enabled} onChange={() => setEnabled(addOnKey, true)} />
            Yes
          </label>
          <label className={page.radioOption}>
            <input type="radio" name={`addon-${addOnKey}`} checked={!addOn.enabled} onChange={() => setEnabled(addOnKey, false)} />
            No
          </label>
        </div>
      </fieldset>
      {addOn.enabled && (
        <>
          <div className={page.formGrid}>
            <GoldenImageSelect
              product={product}
              regionId={region?.id ?? ''}
              component={component}
              value={addOn.goldenImage}
              onChange={(goldenImage) => patch(addOnKey, { goldenImage })}
              label={`${name} golden image`}
              idPrefix={`addon-${addOnKey}`}
            />
          </div>
          <NetworkSelect
            product={product}
            environmentId={environment?.id ?? ''}
            regionId={region?.id ?? ''}
            resourceGroupId={resourceGroup?.id ?? ''}
            value={addOn.network}
            onChange={(network) => patch(addOnKey, { network })}
            defaultValue={suggestedNetwork}
            label={`${name} network`}
            idPrefix={`addon-${addOnKey}-net`}
          />
        </>
      )}
    </section>
  )
}

/**
 * The old link or the new link: one required answer for the whole deployment, asked once. It is not tied to Customer Portal or
 * the product's components. The options and their wording come from `LINK_VERSION_OPTIONS`.
 */
function LinkVersionQuestion() {
  const linkVersion = useInfrastructureWizard((s) => s.linkVersion)
  const setLinkVersion = useInfrastructureWizard((s) => s.setLinkVersion)
  return (
    <section className={styles.section} aria-label="Link">
      <fieldset className={styles.cardGroup} role="radiogroup" aria-required="true">
        <legend className={styles.legend}>
          {LINK_VERSION_QUESTION} <span aria-hidden="true" className={page.required}>*</span>
        </legend>
        {LINK_VERSION_OPTIONS.map((option) => (
          <OptionCard
            key={option.value}
            name="addon-link-version"
            value={option.value}
            checked={linkVersion === option.value}
            onChange={() => setLinkVersion(option.value)}
            title={option.label}
            description={option.description}
          />
        ))}
      </fieldset>
    </section>
  )
}

/**
 * The add-ons the product is asked about:
 *  - Advanced Analytics and Bartender (each with a golden image and a network): Process Pro Global only.
 *  - Customer Portal (an existing IIS server): every product line.
 *  - The product's components (data/traverseComponents.ts), each on an existing IIS server: Traverse's WMS, Trav API Portal,
 *    Web API Portal and Admin Portal. Process Pro Global has none.
 *  - Which link the customer needs: every product line.
 * Answering No hides a card and clears anything entered for it.
 */
export function AddOnsStep() {
  const product = useInfrastructureWizard((s) => s.product)
  const rdsMode = useInfrastructureWizard((s) => effectiveServerMode(s.product, s.rdsServer.mode, 'rds'))
  const rdsNetwork = useInfrastructureWizard((s) => s.rdsServer.network)
  // Only a dedicated RDS server has a network choice to suggest. An existing server's network is not one of the choices.
  const suggested = rdsMode === 'dedicated' ? rdsNetwork : null

  return (
    <>
      {asksAnalyticsAddOns(product) && (
        <>
          <AddOnCard addOnKey="advancedAnalytics" question="Will you use Advanced Analytics?" name="Advanced Analytics" component="advancedAnalytics" suggestedNetwork={suggested} />
          <AddOnCard addOnKey="bartender" question="Will you use Bartender?" name="Bartender" component="bartender" suggestedNetwork={suggested} />
        </>
      )}
      {hasProductLine(product) && (
        <>
          <IisAddOnCard addOnKey="customerPortal" question="Does the customer use Customer Portal?" name="Customer Portal" />
          {componentsFor(product).map((component) => (
            <IisAddOnCard key={component.key} addOnKey={component.key} question={component.question} name={component.name} />
          ))}
          <LinkVersionQuestion />
        </>
      )}
    </>
  )
}
